import { DraftPaymentStatus } from "@/apis/gql/types";
import creditorServiceColumn from "@/components/shared/cells/creditor-service-cell";
import { SelectCell } from "@/components/shared/cells/select";
import {
  InvoiceTransactionLinkStatusIcon,
  type LinkStatus,
  getLinkStatusForInvoice,
} from "@/components/shared/custom-icons/invoice-transaction-link-status-icon";
import { SelectHeader } from "@/components/shared/select-inputs/select";
import { DataTableColumnHeader } from "@/components/shared/tables/ColumnHeader";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
} from "@/components/ui/select";
import { InvoiceDateCell } from "@/features/invoice-overview/cells/invoice-date";
import { InvoiceDateHeader } from "@/features/invoice-overview/headers/invoice-date";
import {
  BankTransferStatusCell,
  getTransferStatus,
} from "@/features/invoices-table/cells/bank-transfers";
import { CostCentersCell } from "@/features/invoices-table/cells/cost-centers";
import { CreatedAtCell } from "@/features/invoices-table/cells/created-at";
import { SmbReviewStatusCell } from "@/features/invoices-table/cells/smb-review-status";
import { StatusBadge } from "@/features/invoices-table/cells/status-badge";
import { TotalAmountCell } from "@/features/invoices-table/cells/total-amount";
import invoiceNoColumn from "@/features/invoices-table/columns/invoice-number-column";
import paymentDeadlineColumn from "@/features/invoices-table/columns/payment-deadline-column";
import useReconEnabled from "@/hooks/use-recon-enabled";
import { useNavigateToInvoiceWithBrowsingQueue } from "@/hooks/useNavigateToInvoiceWithBrowsingQueue";
import { usePermissions } from "@/hooks/usePermissions";
import { InvoiceStatus } from "@/lib/constants/invoiceStatus";
import { useTenant } from "@/providers/session";
import type { GetAllInvoiceInvoice } from "@/queries/invoices/get-all";
import { useUpdateInvoice } from "@/queries/invoices/update";
import type { ColumnDef, Row } from "@tanstack/react-table";
import { isWithinInterval } from "date-fns";
import { CircleOff, Eye, Pencil, SendHorizonal } from "lucide-react";
import { useTranslation } from "react-i18next";

const selectColumn: ColumnDef<GetAllInvoiceInvoice> = {
  id: "select",
  size: 45,
  header: (header) => <SelectHeader {...header} />,
  cell: (cell) => (
    <div className="flex items-center justify-center h-full w-full">
      <SelectCell {...cell} />
    </div>
  ),
  enableSorting: false,
  enableHiding: false,
};

const createdAtColumn: ColumnDef<GetAllInvoiceInvoice> = {
  id: "createdAt",
  size: 130,
  accessorKey: "invoice.createdAt",
  filterFn: (row, columnId, value) => {
    if (!value) return true;

    return isWithinInterval(new Date(row.original.invoice.createdAt), {
      start: value.from,
      end: value.to,
    });
  },
  header: (header) => {
    const { t } = useTranslation();

    return (
      <DataTableColumnHeader column={header.column} title={t("created_at")} />
    );
  },
  cell: (cell) => {
    return <CreatedAtCell createdAt={cell.row.original.invoice.createdAt} />;
  },
};

const totalAmountColumn: ColumnDef<GetAllInvoiceInvoice> = {
  accessorKey: "invoice.totalAmount",
  id: "totalAmount",
  size: 100,
  header: (header) => {
    const { t } = useTranslation();

    return (
      <DataTableColumnHeader
        column={header.column}
        title={t("invoice_total_amount")}
      />
    );
  },
  cell: (cell) => {
    return <TotalAmountCell invoice={cell.row.original.invoice} />;
  },
};

const costCentersColumn: ColumnDef<GetAllInvoiceInvoice> = {
  accessorKey: "costCenters",
  id: "costCenters",
  size: 200,
  header: (header) => {
    const { t } = useTranslation();

    return (
      <DataTableColumnHeader
        column={header.column}
        title={t("cost_centers.column")}
      />
    );
  },
  cell: (cell) => {
    return (
      <CostCentersCell
        invoice={cell.row.original.invoice}
        lineItems={cell.row.original.lineItems}
      />
    );
  },
};

const smbReviewStatus: ColumnDef<GetAllInvoiceInvoice> = {
  id: "smbReviewStatus",
  accessorKey: "invoice.smbReviewStatus",
  size: 130,
  filterFn: (row, columnId, value) => {
    if (!value || value.length === 0) return true;

    const status = row.original?.invoice.smbReviewStatus;

    return value.includes(status);
  },
  header: (header) => {
    const { t } = useTranslation();

    return (
      <DataTableColumnHeader
        column={header.column}
        title={t("smb_review_status.title")}
      />
    );
  },
  cell: (cell) => {
    return <SmbReviewStatusCell {...cell} />;
  },
};

const isPaid: ColumnDef<GetAllInvoiceInvoice> = {
  id: "isPaid",
  accessorKey: "invoice.isPaid",
  size: 193,
  header: (header) => {
    const { t } = useTranslation();

    return (
      <DataTableColumnHeader
        column={header.column}
        title={t("payables.is_paid_header")}
      />
    );
  },
  cell: (cell) => {
    const { t } = useTranslation();
    const [updateInvoice] = useUpdateInvoice();
    const value = cell.getValue();

    const handleChange = (newValue: string) => {
      updateInvoice({
        invoiceId: cell.row.original.invoiceDetailsId,
        isPaid: newValue === "true",
      });
    };

    const invoice = cell.row.original.invoice;

    const isProcessing = invoice?.status === InvoiceStatus.NEW;

    if (isProcessing) return null;

    return (
      <Select
        value={value === true ? "true" : "false"}
        onValueChange={handleChange}
      >
        <SelectTrigger
          className="inline-flex items-center gap-2 whitespace-nowrap rounded-md w-full h-auto px-2 py-2 justify-between font-normal text-sm border-none shadow-none focus-visible:ring-0 focus-visible:ring-offset-0 bg-transparent hover:bg-gray-200 transition-colors"
          aria-label={t("is_paid")}
        >
          {value === true ? (
            <StatusBadge
              icon={CircleOff}
              iconColor="text-gray-800"
              textColor="text-gray-800"
              containerColor="bg-gray-100 border border-gray-200"
            >
              {t("transfer_states.no_transfer")}
            </StatusBadge>
          ) : (
            <StatusBadge
              icon={SendHorizonal}
              iconColor="text-orange-600"
              textColor="text-orange-800"
              containerColor="bg-orange-100 border border-orange-200"
            >
              {t("transfer_states.to_transfer")}
            </StatusBadge>
          )}
        </SelectTrigger>
        <SelectContent className="w-[290px] p-1">
          <SelectItem
            value="false"
            className="py-3 px-2 cursor-pointer"
            hideIndicator
          >
            <div className="flex items-center gap-3">
              <SendHorizonal className="h-4 w-4 text-orange-600" />
              <div className="flex flex-col">
                <div className="font-medium text-foreground">
                  {t("transfer_states.to_transfer")}
                </div>
                <div className="text-xs text-muted-foreground mt-0.5">
                  {t("transfer_states.to_transfer_description")}
                </div>
              </div>
            </div>
          </SelectItem>
          <SelectItem
            value="true"
            className="py-3 px-2 cursor-pointer"
            hideIndicator
          >
            <div className="flex items-center gap-3">
              <CircleOff className="h-4 w-4 text-gray-800" />
              <div className="flex flex-col">
                <div className="font-medium text-foreground">
                  {t("transfer_states.no_transfer")}
                </div>
                <div className="text-xs text-muted-foreground mt-0.5">
                  {t("transfer_states.no_transfer_description")}
                </div>
              </div>
            </div>
          </SelectItem>
        </SelectContent>
      </Select>
    );
  },
};

const actionsColumn: ColumnDef<GetAllInvoiceInvoice> = {
  id: "actions",
  size: 210,
  header: (header) => {
    return <DataTableColumnHeader column={header.column} title={"Aktionen"} />;
  },
  cell: (cell) => {
    const navigate = useNavigateToInvoiceWithBrowsingQueue(cell.table);
    const isProcessing = cell.row.original.invoice.status === InvoiceStatus.NEW;

    async function handleClick() {
      const invoiceId = cell.row.original.invoiceDetailsId;
      navigate(invoiceId);
    }

    const buttonText = isProcessing
      ? "Manuell bearbeiten"
      : "Öffnen zum Prüfen";

    const Icon = isProcessing ? Pencil : Eye;

    return (
      <Button
        size="sm"
        className="bg-gray-100 text-gray-800 hover:bg-gray-300 active:bg-gray-300 focus:bg-gray-300 border border-gray-300"
        onClick={handleClick}
      >
        <Icon className="w-4 h-4 mr-2" />
        {buttonText}
      </Button>
    );
  },
  enableSorting: false,
  enableHiding: false,
};

const invoiceDateColumn: (
  showConfidence: boolean,
) => ColumnDef<GetAllInvoiceInvoice> = (showConfidence) => ({
  id: "invoiceDate",
  accessorKey: "invoice.invoiceDate",
  size: 120,
  header: (header) => <InvoiceDateHeader {...header} />,
  sortingFn: (a, b) => {
    const invoiceDateA = a.original?.invoice.invoiceDate;
    const invoiceDateB = b.original?.invoice.invoiceDate;
    // Convert date strings to Date objects, using a large date for undefined/null
    const dateA = invoiceDateA
      ? new Date(invoiceDateA)
      : new Date(8640000000000000);
    const dateB = invoiceDateB
      ? new Date(invoiceDateB)
      : new Date(8640000000000000);

    // Compare the dates
    return dateA.getTime() - dateB.getTime();
  },
  cell: (cell) => {
    const { isSuperAdmin } = usePermissions();
    const { isOcrReviewRequired } = useTenant();

    if (
      isOcrReviewRequired &&
      !cell.row.original.invoice.ocrReviewed &&
      !isSuperAdmin
    )
      return null;
    return (
      <InvoiceDateCell
        invoice={cell.row.original.invoice}
        showConfidence={showConfidence}
      />
    );
  },
});

const banktransferStatusColumn: ColumnDef<GetAllInvoiceInvoice> = {
  id: "paymentStatus",
  accessorFn: (row) => getTransferStatus(row.invoice, row.draftPayments),
  size: 160,
  header: (header) => {
    const { t } = useTranslation();

    return (
      <DataTableColumnHeader
        column={header.column}
        title={t("transfer-status")}
      />
    );
  },
  cell: (cell) => {
    return (
      <BankTransferStatusCell
        invoice={cell.row.original.invoice}
        draftPayments={cell.row.original.draftPayments}
      />
    );
  },
  sortingFn: (rowA, rowB) => {
    function getPaymentStatusOrder(row: Row<GetAllInvoiceInvoice>) {
      const invoice = row.original.invoice;
      const draftPayments = row.original.draftPayments || [];
      const isPaid = invoice?.isPaid;
      const wasTransfered = draftPayments.some(
        (payment) => payment.status !== DraftPaymentStatus.DRAFT,
      );
      if (!isPaid) {
        return 0;
      }
      if (wasTransfered) {
        return 1;
      }
      return 2;
    }

    const orderA = getPaymentStatusOrder(rowA);
    const orderB = getPaymentStatusOrder(rowB);

    if (orderA < orderB) return -1;
    if (orderA > orderB) return 1;
    return 0;
  },
};

function transactionsColumn(
  reconEnabled: boolean,
): ColumnDef<GetAllInvoiceInvoice, LinkStatus> {
  return {
    id: "transactions",
    accessorFn: (row) =>
      getLinkStatusForInvoice(
        row.transactions,
        row.invoice.reconTriggered,
        reconEnabled,
        row.invoice.status,
      ),
    size: 65,
    header: (header) => (
      <DataTableColumnHeader column={header.column} title="Match" />
    ),
    cell: (cell) => {
      const invoice = cell.row.original.invoice;

      const linkStatus = getLinkStatusForInvoice(
        cell.row.original.transactions,
        invoice?.reconTriggered,
        reconEnabled,
        invoice.status,
      );
      return <InvoiceTransactionLinkStatusIcon linkStatus={linkStatus} />;
    },
  };
}

type ColumnsInput = {
  hasCostCenters: boolean;
  reconEnabled: boolean;
};

export function getColumnsForToReview({
  hasCostCenters,
  reconEnabled,
}: ColumnsInput): ColumnDef<GetAllInvoiceInvoice, any>[] {
  return [
    selectColumn,
    creditorServiceColumn<GetAllInvoiceInvoice>(true),
    totalAmountColumn,
    transactionsColumn(reconEnabled),
    isPaid,
    paymentDeadlineColumn<GetAllInvoiceInvoice>(),
    ...(hasCostCenters ? [costCentersColumn] : []),
    invoiceDateColumn(false),
    invoiceNoColumn<GetAllInvoiceInvoice>(),
    createdAtColumn,
    actionsColumn,
  ];
}

export function getColumnsForReviewed({
  hasCostCenters,
  reconEnabled,
}: ColumnsInput): ColumnDef<GetAllInvoiceInvoice, any>[] {
  return [
    selectColumn,
    creditorServiceColumn<GetAllInvoiceInvoice>(true),
    totalAmountColumn,
    transactionsColumn(reconEnabled),
    isPaid,
    paymentDeadlineColumn<GetAllInvoiceInvoice>(),
    ...(hasCostCenters ? [costCentersColumn] : []),
    invoiceDateColumn(false),
    invoiceNoColumn<GetAllInvoiceInvoice>(),
    createdAtColumn,
  ];
}

export function getColumnsForAll({
  hasCostCenters,
  reconEnabled,
}: ColumnsInput): ColumnDef<GetAllInvoiceInvoice, any>[] {
  return [
    selectColumn,
    creditorServiceColumn<GetAllInvoiceInvoice>(true),
    smbReviewStatus,
    totalAmountColumn,
    transactionsColumn(reconEnabled),
    banktransferStatusColumn,
    paymentDeadlineColumn<GetAllInvoiceInvoice>(),
    ...(hasCostCenters ? [costCentersColumn] : []),
    invoiceDateColumn(false),
    invoiceNoColumn<GetAllInvoiceInvoice>(),
    createdAtColumn,
  ];
}
